/* eslint-disable react-refresh/only-export-components --
 * The button and the error class it documents are intentionally co-located.
 */
import React from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';

import { useIsMobile } from '../../hooks/useIsMobile';
import { useStore } from '../../store';
import {
  formatElapsed,
  formatExportEstimate,
} from '../../utils/exportEstimate';

export type ExportFormat = 'xlsx' | 'csv';

export class UserFacingExportError extends Error {}

const FORMAT_LABELS: Record<ExportFormat, string> = {
  xlsx: 'Excel (.xlsx)',
  csv: 'CSV (.csv)',
};

const FORMAT_NAMES: Record<ExportFormat, string> = {
  xlsx: 'Excel',
  csv: 'CSV',
};

// Resolved at render, not import: a test that partially mocks lucide-react
// must still be able to import this module.
function FormatIcon({ format }: { format: ExportFormat }) {
  return format === 'xlsx' ? <FileSpreadsheet /> : <FileText />;
}

const DEFAULT_FORMATS: readonly ExportFormat[] = ['xlsx', 'csv'];

const TOAST_TICK_MS = 1000;
// The bar never claims completion on its own: a slow request parks it here.
const PROGRESS_HOLD = 0.9;
// "Downloaded" is a receipt, not a message to read: it leaves quickly.
const DOWNLOADED_TOAST_MS = 2000;

interface Props {
  /** Performs the download. Rejects on failure; this component owns the message. */
  onExport: (format: ExportFormat) => Promise<void>;
  disabled?: boolean;
  /**
   * Menu items, in order. Defaults to Excel then CSV. A single format drops
   * the menu: the click exports and the tooltip reads "Export to <format>".
   */
  formats?: readonly ExportFormat[];
  /**
   * Expected duration. When given, the tooltip and each menu item show it and
   * the export runs under a progress toast; without it the button behaves as
   * before.
   */
  estimateSeconds?: number;
  /**
   * Visible text beside the icon, for a page whose users need the action
   * spelled out. Without it the button is icon-only with a tooltip.
   */
  label?: string;
}

/**
 * Icon-only Export button. With several formats it opens an Excel/CSV menu;
 * with one it exports on click and the tooltip names that format.
 *
 * At most two clicks to a file. A modal with a radio group and a confirm
 * button would be three, which is why AdoptionReportExportFormatDialog is not
 * reused here.
 */
export function ExportMenuButton({
  onExport,
  disabled,
  formats = DEFAULT_FORMATS,
  estimateSeconds,
  label,
}: Props) {
  // 44px touch target on phones; the toolbar's 32px icon size on desktop.
  const isMobile = useIsMobile();
  const [isExporting, setIsExporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const addToast = useStore(state => state.addToast);
  const updateToast = useStore(state => state.updateToast);
  const removeToast = useStore(state => state.removeToast);

  // The progress tick outlives a render, so an unmount mid-export clears it.
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimer = () => {
    if (timerRef.current !== null) clearInterval(timerRef.current);
    timerRef.current = null;
  };
  React.useEffect(() => stopTimer, []);

  const estimateLabel =
    estimateSeconds === undefined
      ? null
      : formatExportEstimate(estimateSeconds);

  const startProgressToast = (format: ExportFormat) => {
    if (estimateSeconds === undefined) return null;
    const startedAt = Date.now();
    const id = addToast({
      title: `Preparing ${FORMAT_NAMES[format]} file, ${estimateLabel}`,
      description: formatElapsed(0),
      loading: true,
      progress: 0,
    });
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      updateToast(id, {
        description: formatElapsed(elapsed),
        progress: Math.min(
          elapsed / Math.max(estimateSeconds, 1),
          PROGRESS_HOLD
        ),
      });
    }, TOAST_TICK_MS);
    return {
      succeed: () => {
        stopTimer();
        updateToast(id, {
          title: 'Downloaded',
          description: undefined,
          loading: false,
          progress: undefined,
          duration: DOWNLOADED_TOAST_MS,
        });
      },
      fail: () => {
        stopTimer();
        removeToast(id);
      },
    };
  };

  const run = async (format: ExportFormat) => {
    setError(null);
    setIsExporting(true);
    const toast = startProgressToast(format);
    try {
      await onExport(format);
      toast?.succeed();
    } catch (err) {
      toast?.fail();
      // Never surface the raw server string: a 5xx body can carry a stack
      // trace or a column name. The caller has already routed any surfaceable
      // 4xx detail through getUserFacingApiMessageAsync.
      console.error('Export failed', err);
      setError(
        err instanceof UserFacingExportError
          ? err.message
          : 'Unable to export. Please try again.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  // One format needs no menu: the click is the export, and the hover text
  // names it so the tooltip and the click agree.
  const onlyFormat = formats.length === 1 ? formats[0] : null;
  const buttonLabel =
    label ??
    (onlyFormat === null ? 'Export' : `Export to ${FORMAT_NAMES[onlyFormat]}`);
  const iconSize = isMobile ? 'iconLg' : 'iconMd';

  const button = (
    <Button
      type='button'
      variant='outline'
      size={label === undefined ? iconSize : isMobile ? 'default' : 'sm'}
      aria-label={buttonLabel}
      disabled={disabled || isExporting}
      onClick={onlyFormat === null ? undefined : () => void run(onlyFormat)}
    >
      {isExporting ? (
        <Loader2 className='animate-spin' aria-hidden='true' />
      ) : (
        <Download aria-hidden='true' />
      )}
      {label}
    </Button>
  );

  // A label with the duration estimate under it, when there is one.
  const withEstimate = (label: string) =>
    estimateLabel === null ? (
      label
    ) : (
      <span className='flex flex-col'>
        {label}
        <span className='text-xs text-muted-foreground'>{estimateLabel}</span>
      </span>
    );

  const tooltip = (trigger: React.ReactNode) => (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        {!error && (
          <TooltipContent side='bottom'>
            {withEstimate(buttonLabel)}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Popover
      open={Boolean(error)}
      onOpenChange={open => !open && setError(null)}
    >
      <PopoverAnchor asChild>
        <div className='shrink-0'>
          {onlyFormat !== null ? (
            tooltip(button)
          ) : (
            <DropdownMenu>
              {tooltip(
                <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
              )}
              <DropdownMenuContent align='end'>
                {formats.map(format => (
                  <DropdownMenuItem
                    key={format}
                    onClick={() => void run(format)}
                  >
                    <FormatIcon format={format} />
                    {withEstimate(FORMAT_LABELS[format])}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </PopoverAnchor>
      {error ? (
        <PopoverContent
          asChild
          align='end'
          sideOffset={8}
          collisionPadding={16}
          onOpenAutoFocus={event => event.preventDefault()}
          onFocusOutside={event => event.preventDefault()}
          onCloseAutoFocus={event => event.preventDefault()}
        >
          <Alert
            role='alert'
            variant='destructive'
            className='w-72 max-w-[calc(100vw-2rem)]'
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
